import React, { Component, ReactNode } from 'react';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { DeviceType, Platform, WriteDirection } from 'sofa-reborn-react';
import { Product } from '@ikea-aoa/component-view-3d';
import {
	BodyStyle,
	ButtonStyle,
	SelectedTextStyle,
	DisclaimerStyle,
	IconStyle,
	DisclaimerTextStyle,
	SelectedTextSignStyle,
	SelectedPriceStyle,
	SpacingStyle,
} from './ArmrestPickerStyle';
import {
	KompisPriceFormatter,
	KompisText,
	KompisIcon,
	KompisFocusOutline,
} from '@inter-ikea-kompis/react-components';
import { InformationCircleIcon } from '@inter-ikea-kompis/icons';
import { localeService } from '../../services';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';
import { FocusOutlinePositionWatcherEnum } from '@inter-ikea-kompis/component-focus-outline';
import SkapaModal, {
	Sheets as SkapaSheets,
	ModalHeader as SkapaModalHeader,
	ModalBody as SkapaModalBody,
} from '@ingka/modal';

type Props = {
	showSheet: boolean;
	platform: Platform;
	writeDirection: WriteDirection;
	regularArmrest: Product;
	wideArmrest: Product;
	selectedArmrest: Product | null;
	armrestData: {
		regular: { measure: string | undefined; price: number | undefined };
		wide: { measure: string | undefined; price: number | undefined };
	} | null;
	deviceType: DeviceType;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	onClose?: () => void;
	setRegularArmrests?: () => void;
	setWideArmrests?: () => void;
};

/**
 * React component.
 *
 */
export default class ArmrestPicker extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;
		let priceDiff = 0;
		if (props.armrestData && props.armrestData.regular.price && props.armrestData.wide.price) {
			priceDiff = (props.armrestData.regular.price - props.armrestData.wide.price) * 2;
		}
		const { onClose, setRegularArmrests, setWideArmrests } = props;

		return (
			<SkapaModal visible={props.showSheet} handleCloseBtn={(): void => onClose && onClose()}>
				<SkapaSheets
					size={'small'}
					alignment={'right'}
					header={
						<SkapaModalHeader title={localeService.getTranslation('armrestSelectorHeader')} />
					}
					footer={null}
					style={{
						zIndex: 10,
						borderTop:
							SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
						borderBottom:
							SkapaTheme.border.thickness.thin + 'px solid' + SkapaTheme.color.border.default,
					}}
				>
					<SkapaModalBody>
						{props.armrestData && (
							<BodyStyle>
								<KompisFocusOutline
									theme={SkapaTheme}
									positionWatcher={FocusOutlinePositionWatcherEnum.auto}
								>
									<ButtonStyle
										onClick={props.regularArmrest && setRegularArmrests}
										onKeyDown={(e): void => {
											if (props.regularArmrest && (e.key === 'Enter' || e.key === ' ')) {
												setRegularArmrests && setRegularArmrests();
											}
										}}
										selected={
											props.regularArmrest && props.regularArmrest === props.selectedArmrest
										}
										disabled={props.regularArmrest ? false : true}
										platform={props.platform}
										role="button"
										tabIndex={0}
									>
										{props.regularArmrest?.images && props.regularArmrest?.images[0].src && (
											<img
												src={props.regularArmrest?.images?.[0].src}
												alt={localeService.getTranslation('regularArmrest')}
											/>
										)}
										<SelectedTextStyle>
											<KompisText
												theme={SkapaTheme}
												type={ThemeFontStyleTypeEnum.bodyMedium}
												bold={true}
											>
												{localeService.getTranslation('armrestSelectorRegular')}
											</KompisText>
											<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium}>
												{props.armrestData.regular.measure}
											</KompisText>
										</SelectedTextStyle>
										{priceDiff !== 0 && (
											<SelectedPriceStyle writeDirection={props.writeDirection}>
												<KompisText
													theme={SkapaTheme}
													type={ThemeFontStyleTypeEnum.bodyMedium}
													bold={true}
													className={'armrestPickerPriceContainer'}
												>
													<SelectedTextSignStyle>{priceDiff > 0 ? '+' : '-'}</SelectedTextSignStyle>
													{props.kompisSettings && props.kompisTranslations && priceDiff > 0 && (
														<KompisPriceFormatter
															amount={Math.abs(priceDiff)}
															settings={props.kompisSettings}
														/>
													)}
												</KompisText>
												<KompisText
													theme={SkapaTheme}
													type={ThemeFontStyleTypeEnum.bodyMedium}
													bold={true}
												>
													&nbsp;
												</KompisText>
											</SelectedPriceStyle>
										)}
									</ButtonStyle>
								</KompisFocusOutline>
								<SpacingStyle />
								<KompisFocusOutline
									theme={SkapaTheme}
									positionWatcher={FocusOutlinePositionWatcherEnum.auto}
								>
									<ButtonStyle
										onClick={props.wideArmrest && setWideArmrests}
										onKeyDown={(e): void => {
											if (props.wideArmrest && (e.key === 'Enter' || e.key === ' ')) {
												setWideArmrests && setWideArmrests();
											}
										}}
										selected={props.wideArmrest && props.wideArmrest === props.selectedArmrest}
										disabled={props.wideArmrest ? false : true}
										platform={props.platform}
										role="button"
										tabIndex={0}
									>
										{props.wideArmrest?.images && props.wideArmrest?.images[0].src && (
											<img
												src={props.wideArmrest?.images?.[0].src}
												alt={localeService.getTranslation('wideArmrest')}
											/>
										)}
										<SelectedTextStyle>
											<KompisText
												theme={SkapaTheme}
												type={ThemeFontStyleTypeEnum.bodyMedium}
												bold={true}
											>
												{localeService.getTranslation('armrestSelectorWide')}
											</KompisText>
											<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium}>
												{props.armrestData.wide.measure}
											</KompisText>
										</SelectedTextStyle>
										{priceDiff !== 0 && (
											<SelectedPriceStyle writeDirection={props.writeDirection}>
												<KompisText
													theme={SkapaTheme}
													type={ThemeFontStyleTypeEnum.bodyMedium}
													bold={true}
													className={'armrestPickerPriceContainer'}
												>
													<SelectedTextSignStyle>{priceDiff < 0 ? '+' : '-'}</SelectedTextSignStyle>
													{props.kompisSettings && props.kompisTranslations && priceDiff < 0 && (
														<KompisPriceFormatter
															amount={Math.abs(priceDiff)}
															settings={props.kompisSettings}
														/>
													)}
												</KompisText>
												<KompisText
													theme={SkapaTheme}
													type={ThemeFontStyleTypeEnum.bodyMedium}
													bold={true}
												>
													&nbsp;
												</KompisText>
											</SelectedPriceStyle>
										)}
									</ButtonStyle>
								</KompisFocusOutline>
								<DisclaimerStyle>
									<IconStyle>
										<KompisIcon icon={InformationCircleIcon || ''} theme={SkapaTheme} />
									</IconStyle>
									<DisclaimerTextStyle writeDirection={props.writeDirection}>
										<KompisText theme={SkapaTheme} type={ThemeFontStyleTypeEnum.bodyMedium}>
											{localeService.getTranslation('armrestSelectorDisclaimer')}
										</KompisText>
									</DisclaimerTextStyle>
								</DisclaimerStyle>
							</BodyStyle>
						)}
					</SkapaModalBody>
				</SkapaSheets>
			</SkapaModal>
		);
	}
}
